import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { build, pool, type BuildTask } from '@fitogest/reactive-form-builder/utils';
import { environment } from '../../../../../../environments/environment';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TratamientosApiService } from '../../../../../service/api/tratamientos-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { VisitasVariedadMesApiService } from '../../../../../service/api/visitas_variedad_mes-api.service';
import { VisitasVariedadMesModel } from '../../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import moment from 'moment';

import {
    FormType,
    type DropdownData,
    type FormField,
    type ReactiveDropdownDataSourceContext,
    type ReactiveFormChangedField,
    type ReactiveFormLifecycleEvent,
    type ReactiveFormStableValueChange,
    type ReactiveFormValue,
    type Value,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaSiembraModel } from '../../../../../models/tareas/tarea-siembra.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import { MaquinariaModel } from '../../../../../models/trabajadores/maquinaria.model';
import { SiembraFormDefinition } from './form-control/form-definition';
import { FincasModel } from 'src/app/models/form-common/fincas.module';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const EXTERNAL_DROPDOWN_FINALIZATION_MAX_ATTEMPTS = 8;
const EXTERNAL_DROPDOWN_FINALIZATION_RETRY_MS = 50;

@Component({
    selector: 'app-nueva-carga',
    standalone: false,
    templateUrl: './siembra-form.component.html',
    styleUrls: ['./siembra-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiembraFormComponent extends BaseForm<TareaSiembraModel> implements OnInit {
    public readonly appName = environment.appName;
    public readonly applicationType = environment.features.applicationType;
    public readonly horticola = environment.features.horticola;
    public readonly showTareasSiembraProductos = environment.features.showTareasSiembraProductos;
    public readonly showTareasSiembraMaquinarias = environment.features.showTareasSiembraMaquinarias;
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de siembra...';

    public override model: TareaSiembraModel = {};
    public initialPreviousData: TareaSiembraModel | null = null;
    public form!: SiembraFormDefinition;
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public formRecordId = '';
    public getRequest = this.tareasApi.siembra.GET;

    private isExternalDropdownFinalizationScheduled = false;
    private lastExternalDropdownFinalizationSignature = '';

    constructor(
        public override route: ActivatedRoute,
        public override router: Router,
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public clientesApi: ClientesApiService,
        public fincasApi: FincasApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
        public productosApi: ProductosApiService,
        public tratamientosApi: TratamientosApiService,
        public visitasVariedadMesApi: VisitasVariedadMesApiService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.siembra.PUT,
            tareasApi.siembra.POST,
            'tareas-siembra',
            environment.features.horticola ? 'Actualizar Cultivo' : 'Actualizar Tarea Siembra',
            environment.features.horticola ? 'Crear Cultivo' : 'Realizar Tarea Siembra',
            environment.features.horticola ? 'Duplicar Cultivo' : 'Duplicar Tarea Siembra',
        );
    }

    public ngOnInit(): void {
        void this.initializeSiembraForm();
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareaSiembraModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            ...event.value,
        });
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD) {
            this.finalizeAndRefreshExternalDropdowns();
            return;
        }

        if (changedFieldName === '__dropdown_refresh') {
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleFormValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<TareaSiembraModel>,
        tag: ReactiveFormChangedField<TareaSiembraModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: tag,
            changedFields: [String(tag ?? '')],
            origin: 'user',
        });
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaSiembraModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            ...event.value,
        });
        this.syncModelFromForm();
        this.form.updateCultivoVariedadVisibility();
        this.applyHorticolaHydrationSeeds(event.value);
        this.cdr.markForCheck();
    }

    public handleFormValueChange(
        formModel: ReactiveFormValue<TareaSiembraModel>,
        tag: ReactiveFormChangedField<TareaSiembraModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_cliente') {
            this.clearSiembraCascade(['id_finca', 'id_sector', 'ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_finca') {
            this.clearSiembraCascade(['id_sector', 'ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_sector') {
            this.clearSiembraCascade(['ids_parcelas', 'id_cultivo_precedente']);
        }

        if (changedFieldName === 'ids_parcelas') {
            this.clearSiembraCascade(['id_cultivo_precedente']);
        }

        if (changedFieldName === 'id_cultivo') {
            this.clearSiembraCascade(['id_variedad']);
            this.form.updateCultivoVariedadVisibility();
            this.cdr.markForCheck();
        }
    }

    private async initializeSiembraForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.formRecordId = this.resolveRouteRecordId();
            this.updateHostModel(this.toSiembraModel(this.resolveRouteModel()));
            this.initialPreviousData = this.getInitialPreviousData();
            this.applyInitialClient();

            this.form = new SiembraFormDefinition(this.getType(), this.model);
            this.configureHorticolaDeclarativeCatalogs();
            this.syncModelFromForm();

            await this.loadCriticalDropdowns();
            this.applySingleFincaDefault();
            this.loadingData = false;
            this.showReactiveForm();

            void this.loadDeferredDropdowns()
                .catch((error: unknown): void => {
                    console.warn('[siembra dropdowns] deferred dropdowns failed.', error);
                });
        } catch (error: unknown) {
            this.handleInitialisationError(error);
        }
    }

    private showReactiveForm(): void {
        this.isReactiveFormReady = true;
        this.loadingData = false;
        this.cdr.detectChanges();
        this.finalizeAndRefreshExternalDropdowns();
        this.cdr.markForCheck();
    }

    private finalizeAndRefreshExternalDropdowns(): void {
        if (!this.isReactiveFormReady) {
            return;
        }

        this.externalDropdownsReady = true;

        if (this.isExternalDropdownFinalizationScheduled) {
            return;
        }

        this.isExternalDropdownFinalizationScheduled = true;
        globalThis.setTimeout((): void => {
            this.isExternalDropdownFinalizationScheduled = false;
            this.scheduleExternalDropdownFinalization();
        }, 0);
    }

    private scheduleExternalDropdownFinalization(attempt = 0): void {
        if (this.formBuilderRef) {
            const nextSignature = this.createExternalDropdownFinalizationSignature();

            if (nextSignature === this.lastExternalDropdownFinalizationSignature) {
                this.cdr.markForCheck();

                return;
            }

            this.lastExternalDropdownFinalizationSignature = nextSignature;
            this.formBuilderRef.finalizeExternalDropdowns();
            this.cdr.markForCheck();

            return;
        }

        if (attempt >= EXTERNAL_DROPDOWN_FINALIZATION_MAX_ATTEMPTS) {
            this.cdr.markForCheck();

            return;
        }

        globalThis.setTimeout((): void => {
            this.scheduleExternalDropdownFinalization(attempt + 1);
        }, EXTERNAL_DROPDOWN_FINALIZATION_RETRY_MS);
    }

    private createExternalDropdownFinalizationSignature(): string {
        return JSON.stringify({
            formRecordId: this.formRecordId,
            idFinca: this.form?.model?.id_finca ?? null,
            idSector: this.form?.model?.id_sector ?? null,
            idsParcelas: this.form?.model?.ids_parcelas ?? null,
            idCultivo: this.form?.model?.id_cultivo ?? null,
            idVariedad: this.form?.model?.id_variedad ?? null,
            dropdowns: {
                clientes: this.form?.clientes.values.length ?? 0,
                fincas: this.form?.fincas.values.length ?? 0,
                sectores: this.form?.sectores.values.length ?? 0,
                parcelas: this.form?.parcelas.values.length ?? 0,
                cultivos: this.form?.cultivos.values.length ?? 0,
                variedades: this.form?.variedades.values.length ?? 0,
                cultivosPrecedentes: this.form?.cultivosPrecedentes.values.length ?? 0,
                trabajadores: this.form?.trabajadores.values.length ?? 0,
                maquinarias: this.form?.maquinarias.values.length ?? 0,
                productos: this.form?.productos.values.length ?? 0,
                tratamientos: this.form?.tratamientos.values.length ?? 0,
            },
        });
    }

    private loadCriticalDropdowns(): Promise<unknown> {
        return pool(this.getCriticalDropdownBuilds());
    }

    private getCriticalDropdownBuilds(): BuildTask[] {
        const builds: BuildTask[] = [
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(
                this.fincasApi.parcelasGET,
                this.form.parcelas,
                this.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
            ),
        ];

        if (this.shouldLoadClientesBeforeRender()) {
            builds.unshift(build(this.clientesApi.clientesGET, this.form.clientes, 'nombre'));
        }

        return builds;
    }

    private async loadDeferredDropdowns(): Promise<void> {
        if (this.horticola) {
            return;
        }

        await pool([
            build(this.trabajadoresApi.trabajadoresGET, this.form.trabajadores,
                (it: MantenimientoModel) => `${it.nombre} ${it.apellidos || ''}`,
                true,
                true),
            build(this.trabajadoresApi.maquinariaGET, this.form.maquinarias, 'nombre', true),
            build(this.productosApi.productos.GET, this.form.productos, 'nombre', true),
            build(this.tratamientosApi.tratamientosList, this.form.tratamientos, 'nombre'),
        ]);

        this.markProductExpirations();
        this.markMachineryExpirations();
        this.finalizeAndRefreshExternalDropdowns();
    }

    private configureHorticolaDeclarativeCatalogs(): void {
        this.configureNonHorticolaDeclarativeCatalogs();

        if (!this.horticola) {
            return;
        }

        const cultivoField = this.findSiembraField('id_cultivo');
        const variedadField = this.findSiembraField('id_variedad');

        if (cultivoField) {
            cultivoField.dataSource = {
                endpoint: this.siexApi.cultivosGET,
                cacheKey: 'siembra-horticola-cultivos',
                labelKey: 'cultivo',
                valueKey: 'id',
                distinctBy: 'id',
            };
        }

        if (variedadField) {
            variedadField.dataSource = {
                endpoint: this.siexApi.variedadesPorCultivoGET,
                cacheKey: 'siembra-horticola-variedades-por-cultivo',
                lazy: true,
                dependsOn: ['id_cultivo'],
                params: (context): Record<string, unknown> => this.getHorticolaVariedadParams(context),
                labelKey: 'variedad',
                valueKey: 'id',
                distinctBy: 'id',
            };
        }

        this.form.formFields = {
            ...this.form.formFields,
            dropdownCatalogs: {
                critical: [],
                deferred: ['id_cultivo'],
                lazy: ['id_variedad'],
            },
        };
    }

    private configureNonHorticolaDeclarativeCatalogs(): void {
        if (this.horticola) {
            return;
        }

        const cultivoPrecedenteField = this.findSiembraField('id_cultivo_precedente');

        if (cultivoPrecedenteField) {
            cultivoPrecedenteField.dataSource = {
                endpoint: this.siexApi.cultivosGET,
                cacheKey: 'siembra-cultivos-precedentes',
                labelKey: 'cultivo',
                valueKey: 'id',
                distinctBy: 'id',
            };
        }

        this.form.formFields = {
            ...this.form.formFields,
            dropdownCatalogs: {
                critical: [],
                deferred: ['id_cultivo_precedente'],
                lazy: [],
            },
        };
    }

    private findSiembraField(fieldName: string): FormField<TareaSiembraModel> | null {
        for (const section of this.form.formFields.sections) {
            const field = section.fields.find((candidate) => candidate.name === fieldName);

            if (field) {
                return field;
            }
        }

        return null;
    }

    private getHorticolaVariedadParams(
        context: ReactiveDropdownDataSourceContext<TareaSiembraModel>,
    ): Record<string, unknown> {
        const selectedCultivo = context.getSelectedRaw<VisitasVariedadMesModel>('id_cultivo');
        const codigoCultivo = this.firstNonEmptyString(
            selectedCultivo?.codigo,
            selectedCultivo?.codigo_cultivo,
            selectedCultivo?.id,
            context.getSelectedBackendValue('id_cultivo'),
            this.readRecordValue(this.form.model, 'id_cultivo'),
        );
        const cultivo = this.firstNonEmptyString(
            selectedCultivo?.cultivo,
            context.getSelectedLabel('id_cultivo'),
            this.readRecordValue(this.form.model, 'cultivo'),
        );

        return {
            codigo_cultivo: codigoCultivo,
            cultivo,
        };
    }

    private applyHorticolaHydrationSeeds(previousData: ReactiveFormValue<TareaSiembraModel> | null): void {
        if (!this.horticola || !previousData) {
            return;
        }

        const source = previousData as Record<string, unknown>;
        const cultivoId = this.firstNonEmptyString(source['id_cultivo']);
        const cultivoCode = this.firstNonEmptyString(source['codigo_cultivo'], source['cultivo_codigo'], cultivoId);
        const cultivoLabel = this.resolveHorticolaCultivoSeedLabel(source, cultivoId, cultivoCode);

        if (cultivoId !== '') {
            const cultivoSeed = new VisitasVariedadMesModel();

            cultivoSeed.id = cultivoId;
            cultivoSeed.codigo = cultivoCode;
            cultivoSeed.codigo_cultivo = cultivoCode;
            cultivoSeed.cultivo = cultivoLabel;
            this.ensureVisitasDropdownSeed(this.form.cultivos, cultivoSeed, cultivoSeed.cultivo, [cultivoCode]);
        }

        const variedadId = this.firstNonEmptyString(source['id_variedad']);
        const variedadCode = this.firstNonEmptyString(source['codigo_variedad'], source['variedad_codigo'], variedadId);
        const variedadLabel = this.resolveHorticolaVariedadSeedLabel(source, variedadId, variedadCode);

        if (variedadId !== '') {
            const variedadSeed = new VisitasVariedadMesModel();

            variedadSeed.id = variedadId;
            variedadSeed.id_variedad = variedadId;
            variedadSeed.codigo = variedadCode;
            variedadSeed.codigo_variedad = variedadCode;
            variedadSeed.codigo_cultivo = cultivoCode;
            variedadSeed.cultivo = cultivoLabel;
            variedadSeed.variedad = variedadLabel;
            this.ensureVisitasDropdownSeed(this.form.variedades, variedadSeed, variedadSeed.variedad, [variedadCode]);
        }

        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private resolveHorticolaCultivoSeedLabel(
        source: Record<string, unknown>,
        cultivoId: string,
        cultivoCode: string,
    ): string {
        return this.resolveSeedDisplayLabel(
            [cultivoId, cultivoCode],
            this.resolveExistingDropdownLabel(this.form.cultivos, cultivoId, cultivoCode),
            source['cultivos_nombres'],
            source['cultivo_nombre'],
            source['nombre_cultivo'],
            this.readNestedRecordLabel(source['id_cultivo'], ['cultivo', 'cultivo_nombre', 'nombre', 'label']),
            this.readNestedRecordLabel(source['ids_parcelas'], ['cultivo', 'cultivo_nombre', 'cultivos_nombres']),
            this.resolveSelectedParcelLabel(
                ['cultivos_nombres', 'cultivo_nombre', 'nombre_cultivo', 'cultivo'],
                [cultivoId, cultivoCode],
            ),
            source['cultivo'],
            cultivoId,
        );
    }

    private resolveHorticolaVariedadSeedLabel(
        source: Record<string, unknown>,
        variedadId: string,
        variedadCode: string,
    ): string {
        return this.resolveSeedDisplayLabel(
            [variedadId, variedadCode],
            this.resolveExistingDropdownLabel(this.form.variedades, variedadId, variedadCode),
            source['variedades_nombres'],
            source['variedad_nombre'],
            source['nombre_variedad'],
            this.readNestedRecordLabel(source['id_variedad'], ['variedad', 'variedad_nombre', 'nombre', 'label']),
            this.readNestedRecordLabel(source['ids_parcelas'], ['variedad', 'variedad_nombre', 'variedades_nombres']),
            this.resolveSelectedParcelLabel(
                ['variedades_nombres', 'variedad_nombre', 'nombre_variedad', 'variedad'],
                [variedadId, variedadCode],
            ),
            source['variedad'],
            variedadId === '0' ? 'SIN VARIEDAD' : '',
            variedadId,
        );
    }

    private resolveSeedDisplayLabel(excludedValues: readonly string[], ...candidates: readonly unknown[]): string {
        const normalizedExcludedValues = excludedValues
            .map((value) => value.trim())
            .filter((value) => value !== '');

        for (const candidate of candidates) {
            const normalizedCandidate = this.firstNonEmptyString(candidate);

            if (normalizedCandidate === '') {
                continue;
            }

            if (normalizedExcludedValues.includes(normalizedCandidate)) {
                continue;
            }

            return normalizedCandidate;
        }

        return this.firstNonEmptyString(...excludedValues);
    }

    private resolveExistingDropdownLabel(
        dropdown: DropdownData<VisitasVariedadMesModel>,
        ...ids: readonly unknown[]
    ): string {
        for (const id of ids) {
            const normalizedId = this.firstNonEmptyString(id);

            if (normalizedId === '') {
                continue;
            }

            const option = dropdown.values.find((candidate) => this.dropdownOptionMatchesId(candidate, normalizedId));

            if (option?.label && option.label.trim() !== normalizedId) {
                return option.label.trim();
            }
        }

        return '';
    }

    private resolveSelectedParcelLabel(keys: readonly string[], excludedValues: readonly string[]): string {
        const selectedParcelIds = this.collectComparableIds(this.form.model.ids_parcelas);
        const normalizedExcludedValues = excludedValues
            .map((value) => this.firstNonEmptyString(value))
            .filter((value) => value !== '');

        for (const option of this.form.parcelas.values) {
            const parcela = option.value;

            if (!parcela || typeof parcela !== 'object') {
                continue;
            }

            const parcelaId = this.firstNonEmptyString((parcela as Record<string, unknown>)['id']);

            if (selectedParcelIds.size > 0 && !selectedParcelIds.has(parcelaId)) {
                continue;
            }

            const label = this.readNestedRecordLabel(parcela, keys);

            if (label !== '' && !normalizedExcludedValues.includes(label)) {
                return label;
            }
        }

        return '';
    }

    private collectComparableIds(value: unknown): Set<string> {
        let sourceValues: unknown[];

        if (Array.isArray(value)) {
            sourceValues = value;
        } else if (typeof value === 'string') {
            sourceValues = value.split(';');
        } else {
            sourceValues = [value];
        }

        const comparableIds = new Set<string>();

        for (const sourceValue of sourceValues) {
            const comparableId = this.getComparableId(sourceValue);

            if (comparableId !== '') {
                comparableIds.add(comparableId);
            }
        }

        return comparableIds;
    }

    private getComparableId(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            const id = record['id'];

            return id === null || id === undefined || id === '' ? '' : String(id);
        }

        return String(value).trim();
    }

    private readNestedRecordLabel(source: unknown, keys: readonly string[]): string {
        if (Array.isArray(source)) {
            for (const item of source) {
                const label = this.readNestedRecordLabel(item, keys);

                if (label !== '') {
                    return label;
                }
            }

            return '';
        }

        if (!source || typeof source !== 'object') {
            return '';
        }

        const record = source as Record<string, unknown>;

        for (const key of keys) {
            const label = this.firstNonEmptyString(record[key]);

            if (label !== '') {
                return label;
            }
        }

        return '';
    }

    private ensureVisitasDropdownSeed(
        dropdown: DropdownData<VisitasVariedadMesModel>,
        seed: VisitasVariedadMesModel,
        label: string,
        aliases: readonly unknown[],
    ): void {
        const seedId = this.firstNonEmptyString(seed.id, seed.id_variedad, seed.codigo, seed.codigo_variedad);

        if (seedId === '') {
            return;
        }

        const normalizedLabel = label.trim() !== '' ? label.trim() : seedId;
        const seedOption: Value<VisitasVariedadMesModel> = {
            value: seed,
            label: normalizedLabel,
            raw: seed,
            valueAliases: aliases.filter((alias) => this.firstNonEmptyString(alias) !== ''),
        };
        const existingIndex = dropdown.values.findIndex((option) => this.dropdownOptionMatchesId(option, seedId));

        if (existingIndex >= 0) {
            dropdown.values[existingIndex] = {
                ...dropdown.values[existingIndex],
                ...seedOption,
            };
        } else {
            dropdown.values = [seedOption, ...dropdown.values];
        }

        dropdown.filtered = dropdown.values;
        dropdown.provenance = dropdown.provenance ?? 'hydrationSeed';
    }

    private dropdownOptionMatchesId(option: Value<VisitasVariedadMesModel>, id: string): boolean {
        const value = option.value as Record<string, unknown> | string | number | null;

        if (value === null) {
            return false;
        }

        if (typeof value !== 'object') {
            return String(value) === id;
        }

        return [value['id'], value['id_variedad'], value['codigo'], value['codigo_variedad']]
            .some((candidate) => this.firstNonEmptyString(candidate) === id);
    }

    private readRecordValue(source: unknown, key: string): unknown {
        if (!source || typeof source !== 'object') {
            return '';
        }

        return (source as Record<string, unknown>)[key];
    }

    private firstNonEmptyString(...values: readonly unknown[]): string {
        for (const value of values) {
            if (value === null || value === undefined) {
                continue;
            }

            const normalizedValue = String(value).trim();

            if (normalizedValue !== '') {
                return normalizedValue;
            }
        }

        return '';
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            horas_totales: horasTotales,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private clearSiembraCascade(fieldNames: string[]): void {
        const updates: Record<string, unknown> = {};

        for (const fieldName of fieldNames) {
            updates[fieldName] = this.getEmptyValueForField(fieldName);
        }

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(updates as Partial<TareaSiembraModel>, false);
        this.cdr.markForCheck();
    }

    private getEmptyValueForField(fieldName: string): unknown {
        if (fieldName === 'id_sector' || fieldName === 'ids_parcelas') {
            return this.horticola ? '' : [];
        }

        return '';
    }

    private getInitialPreviousData(): TareaSiembraModel | null {
        if (this.getType() !== FormType.DUPLICATION) {
            return null;
        }

        return this.toSiembraModel(this.model);
    }

    private resolveRouteRecordId(): string {
        const routeId = this.route.snapshot.paramMap.get('id') ?? this.route.snapshot.params['id'] ?? '';

        return String(routeId ?? '').trim();
    }

    private resolveRouteModel(): Record<string, unknown> {
        return {
            ...this.route.snapshot.params,
            id: this.formRecordId || this.route.snapshot.params['id'] || '',
        };
    }

    private shouldLoadClientesBeforeRender(): boolean {
        return this.form.canSeeClientes && !StorageManager.getClient() && environment.features.hasClients;
    }

    private applyInitialClient(): void {
        const currentClientId = StorageManager.getClient()?.id ?? '';

        if (currentClientId === '') {
            return;
        }

        if (this.model.id_cliente === undefined || this.model.id_cliente === null || this.model.id_cliente === '') {
            this.model.id_cliente = currentClientId;
        }
    }

    private applySingleFincaDefault(): void {
        if (this.getType() !== FormType.CREATION) {
            return;
        }

        const realFincas = this.form.fincas.values
            .map((option) => option.value)
            .filter((value): value is FincasModel => value !== null && typeof value === 'object');

        if (realFincas.length !== 1) {
            return;
        }

        const finca = realFincas[0];

        this.form.model = this.toSiembraModel({
            ...this.form.model,
            id_finca: finca,
        });
        this.syncModelFromForm();

        if (this.isReactiveFormReady) {
            this.formBuilderRef?.updateForm({ id_finca: finca }, false);
        }
    }

    private markProductExpirations(): void {
        if (!this.showTareasSiembraProductos) {
            return;
        }

        for (const product of this.form.productos.filtered) {
            if (!product.value?.fecha_caducidad || product.label.startsWith('*')) {
                continue;
            }

            const expirationDate = moment(product.value.fecha_caducidad, 'DD/MM/YYYY');

            if (expirationDate.isBefore(moment())) {
                product.label = '** ' + product.label;
            } else if (expirationDate.isBefore(moment().add(3, 'months'))) {
                product.label = '* ' + product.label;
            }
        }
    }

    private markMachineryExpirations(): void {
        if (!this.showTareasSiembraMaquinarias) {
            return;
        }

        for (const machinery of this.form.maquinarias.filtered) {
            const value = machinery.value as MaquinariaModel | null;

            if (!value?.fecha_ultima_inspeccion || machinery.label.startsWith('*')) {
                continue;
            }

            const dateParts = value.fecha_ultima_inspeccion.toString().split('/');
            const year = parseInt(dateParts[2] ?? '', 10);
            const increment = parseInt(dateParts[0] ?? '', 10) >= 2020 ? 3 : 5;

            dateParts[2] = (year + increment).toString();
            const expirationDate = moment(dateParts.join('/'), 'DD/MM/YYYY');

            if (expirationDate.isBefore(moment())) {
                machinery.label = '** ' + machinery.label;
            } else if (expirationDate.isBefore(moment().add(3, 'months'))) {
                machinery.label = '* ' + machinery.label;
            }
        }
    }

    private handleInitialisationError(error: unknown): void {
        this.loadingData = false;
        this.finalizeExternalDropdowns();
        this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
        this.cdr.markForCheck();
        console.error('No se ha podido preparar el formulario de siembra.', error);
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de siembra: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de siembra. Revisa la conexión e inténtalo de nuevo.';
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toSiembraModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toSiembraModel(
        sourceModel: ReactiveFormValue<TareaSiembraModel> | Partial<TareaSiembraModel> | Record<string, unknown>,
    ): TareaSiembraModel {
        const normalizedModel: Record<string, TareaSiembraModel[keyof TareaSiembraModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as TareaSiembraModel[keyof TareaSiembraModel];
            }
        }

        return normalizedModel as TareaSiembraModel;
    }


    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev
            ? `${variedad} - ${zonaRiegoAbrev}`
            : variedad;
        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficie = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficieFormatted = superficie.toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const unidades = environment.features.textoSuperficie || 'ha';
        const rightText = [codigos || undefined, `${superficieFormatted} ${unidades}`]
            .filter(Boolean)
            .join(' · ');

        return `${nombre} - ${variedadConZona} — ${rightText}`;
    };
}
